
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CallStatusBadge } from "./CallStatusBadge";
import { Clock, Phone, X, CheckCircle2 } from "lucide-react";
import { formatDistance } from "date-fns";

interface CallStatusInfoCardProps {
  status: string;
  createdAt: string;
  meta?: {
    duration?: number;
    answer_time?: string;
    end_time?: string;
    hangup_cause?: string;
  };
}

export const CallStatusInfoCard = ({ status, createdAt, meta }: CallStatusInfoCardProps) => {
  const getStatusIcon = () => {
    switch (status) {
      case "completed":
      case "reactivated":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "in-progress":
        return <Phone className="h-5 w-5 text-blue-500 animate-pulse" />;
      case "failed":
      case "not-interested":
        return <X className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusDescription = () => {
    switch (status) {
      case "completed":
        return "Call completed successfully";
      case "in-progress":
        return "Call is currently in progress";
      case "queued":
        return "Call is queued and waiting to be made";
      case "reactivated":
        return "Lead has been reactivated";
      case "failed":
        return meta?.hangup_cause || "Call failed to connect";
      case "not-interested":
        return "Lead indicated they are not interested";
      default:
        return "Unknown status";
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return "N/A";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-md flex items-center gap-2">
          {getStatusIcon()}
          Call Status
        </CardTitle>
        <CallStatusBadge status={status} />
      </CardHeader>
      <CardContent className="text-sm space-y-3">
        <p className="text-muted-foreground">{getStatusDescription()}</p>
        
        <div className="grid grid-cols-2 gap-2 text-xs mt-4">
          <div>
            <p className="font-medium">Initiated</p>
            <p className="text-muted-foreground">
              {formatDistance(new Date(createdAt), new Date(), { addSuffix: true })}
            </p>
          </div>
          
          {meta?.duration !== undefined && (
            <div>
              <p className="font-medium">Duration</p>
              <p className="text-muted-foreground">{formatDuration(meta.duration)}</p>
            </div>
          )}
          
          {meta?.answer_time && (
            <div>
              <p className="font-medium">Answered</p>
              <p className="text-muted-foreground">
                {formatDistance(new Date(meta.answer_time), new Date(createdAt), { addSuffix: false })} after dial
              </p>
            </div>
          )}
          
          {meta?.hangup_cause && (
            <div>
              <p className="font-medium">Hangup Reason</p>
              <p className="text-muted-foreground">{meta.hangup_cause}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
